import { VAULT_OBJECT_THUMBNAIL_TOP_PERCENT } from "@/components/vault/vault-object-util";
import { VaultObjectPreviewDto } from "@/types/data-types";
import HENGE from "@henge-inc/global-types";
import HUI from "@henge-inc/ui/v1_2";
import React, { useRef, useState } from "react";
import { DEFAULT_AZIMUTH_ANGLE, DEFAULT_POLAR_ANGLE } from "@/utils/cameraUtil";
import { VAULT_OBJECT_TYPE } from "@/types/enums";
import { FeedObjectLazyImage } from "@/components/vault/slot/FeedObjectLazyImage";
import { Box, Text } from "@radix-ui/themes";

interface VaultObjectProps {
  className: string;
  rowTotal: number;
  rowIndex: number;
  colIndex: number;
  item: VaultObjectPreviewDto | null;
  size?: number;
  pointerEvents?: "auto" | "none";
  onClick?: () => void;
}

export const FeedSlot = React.memo(
  ({
    className,
    rowTotal,
    rowIndex,
    colIndex,
    item,
    size = 1,
    pointerEvents = "auto",
    onClick,
  }: VaultObjectProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const observeTargetRef = useRef<HTMLAnchorElement>(null);

    const [hover, setHover] = useState(false);
    const [imageError, setImageError] = useState(false);

    const thumbnailSize = 80;
    // let thumbnailSize = 50;
    // if (
    //   item &&
    //   item.type === VAULT_OBJECT_TYPE.OBJECT &&
    //   item.thumbnailVersion === "241217"
    // ) {
    //   thumbnailSize = 80;
    // }

    return (
      <div
        ref={ref}
        className={`${className} select-none pointer-events-none relative flex-shrink-0 flex justify-center items-center`}
        draggable={false}
        style={{
          zIndex: rowIndex + (size === 2 ? 0 : 1),
          width: "100%",
          height: "auto",
          aspectRatio: "2000 / 585",
          objectFit: "cover",
        }}
      >
        <div
          className={"absolute -top-1/2 -bottom-1/2 left-0 right-0"}
          style={{
            width: "100%",
            height: "auto",
            aspectRatio: "1000 / 585",
          }}
        >
          <div
            className={"absolute bottom-[3%] left-1/2 -translate-x-1/2"}
            style={{
              width: `${size * 100 - 6}%`,
              height: "auto",
              aspectRatio: "1000 / 585",
              background: `center / cover url(${process.env.ASSET_URL ?? ""}/assets/images/gaia1000.png)`,
            }}
          >
            {item && (
              <>
                {item.thumbnailUrl ? (
                  <div
                    style={{
                      width:
                        item.type === VAULT_OBJECT_TYPE.OBJECT
                          ? `${thumbnailSize}%`
                          : `${thumbnailSize}%`,
                      height:
                        item.type === VAULT_OBJECT_TYPE.OBJECT
                          ? `${thumbnailSize}%`
                          : `${thumbnailSize}%`,
                      zIndex: 2,
                    }}
                    className={
                      "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                    }
                  >
                    <div
                      style={{
                        transform: `translate(-50%, -${VAULT_OBJECT_THUMBNAIL_TOP_PERCENT}%)`,
                      }}
                      className={
                        "absolute top-1/2 left-1/2 w-full h-auto transition-all"
                      }
                    >
                      <FeedObjectLazyImage
                        src={item.thumbnailUrl}
                        name={item.name}
                        observeTargetRef={observeTargetRef}
                        hover={hover}
                        setImageError={setImageError}
                      />
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      pointerEvents,
                      cursor: "pointer",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      width: "67%",
                      height: "auto",
                      aspectRatio: "1 / 1",
                      transform: `translate(-50%, -50%) rotateX(${
                        (DEFAULT_POLAR_ANGLE * 180) / Math.PI
                      }deg) rotateZ(-${(DEFAULT_AZIMUTH_ANGLE * 180) / Math.PI}deg)`,
                      backgroundColor: "transparent",
                      overflow: "hidden",
                      zIndex: 2,
                    }}
                  >
                    <div className={"w-full px-2"}>
                      <HUI.Text
                        kind={HENGE.TEXT_KIND.body1}
                        weight={HENGE.TEXT_WEIGHT.regular}
                      >
                        {item.name}
                      </HUI.Text>
                    </div>
                  </div>
                )}
              </>
            )}
            {/* Tile Boundary */}
            <a
              ref={observeTargetRef}
              style={{
                pointerEvents,
                cursor: "pointer",
                position: "absolute",
                top: "50%",
                left: "50%",
                width: "67%",
                height: "auto",
                aspectRatio: "1 / 1",
                transform: `translate(-50%, -50.5%) rotateX(${
                  (DEFAULT_POLAR_ANGLE * 180) / Math.PI
                }deg) rotateZ(-${(DEFAULT_AZIMUTH_ANGLE * 180) / Math.PI}deg)`,
                backgroundColor: item?.backgroundHex ?? "transparent",
                borderRadius: "4%",
                overflow: "hidden",
                zIndex: 1,
              }}
              onMouseEnter={() => {
                setHover(true);
              }}
              onMouseLeave={() => {
                setHover(false);
              }}
              onClick={(e) => {
                e.preventDefault();

                if (pointerEvents === "none") return;
                if (!item) return;

                onClick?.();
              }}
            >
              {item && imageError && (
                <Text
                  color={"gray"}
                  highContrast
                  align={"right"}
                  className={"block w-full py-1 px-2"}
                >
                  {item.name}
                </Text>
              )}
            </a>
            <span
              style={{
                pointerEvents,
                cursor: "pointer",
                position: "absolute",
                top: "50%",
                left: "50%",
                width: "75%",
                height: "auto",
                aspectRatio: "1 / 1",
                transform: `translate(-50%, -50%) rotateX(${
                  (DEFAULT_POLAR_ANGLE * 180) / Math.PI
                }deg) rotateZ(-${(DEFAULT_AZIMUTH_ANGLE * 180) / Math.PI}deg)`,
                borderTopRightRadius: "6%",
                borderBottomLeftRadius: "6%",
                borderTopLeftRadius: "9%",
                borderBottomRightRadius: "9%",
                border: "1px solid var(--border-tertiary)",
                overflow: "hidden",
                zIndex: 0,
              }}
            />
          </div>

          {rowIndex === rowTotal - 1 && (
            <>
              {colIndex === 0 && <LastRowDummy side={"left"} />}
              {<LastRowDummy />}
            </>
          )}
        </div>
      </div>
    );
  },
);

FeedSlot.displayName = "FeedSlot";

const LastRowDummy = ({ side = "right" }) => (
  <Box
    className={"right-0 overflow-hidden"}
    style={{
      position: "absolute",
      top: "50%",
      left: side === "right" ? "50%" : undefined,
      right: side === "left" ? "50%" : undefined,
      width: "100%",
      height: "auto",
      aspectRatio: "2000 / 585",
      maskImage: "linear-gradient(to bottom, black 33%, transparent)",
    }}
  >
    <Box
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        width: "90%",
        height: "auto",
        aspectRatio: "1 / 1",
        transform: `translate(-50%, -25%) rotateX(${
          (DEFAULT_POLAR_ANGLE * 180) / Math.PI
        }deg) rotateZ(${(DEFAULT_AZIMUTH_ANGLE * 180) / Math.PI}deg)`,
        borderTopRightRadius: "4%",
        borderBottomLeftRadius: "4%",
        borderTopLeftRadius: "6%",
        borderBottomRightRadius: "6%",
        border: "1px solid var(--border-tertiary)",
        background: "#f1f1f1",
        overflow: "hidden",
        zIndex: 0,
      }}
    />
  </Box>
);
